import { useState } from 'react'
import { useNavigate, Navigate } from 'react-router-dom'
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material'
import { useAuth } from '../hooks/useAuth'
import { useT } from '../i18n/useT'
import { useLanguage } from '../i18n/LanguageProvider'
import { FlagDE, FlagEN } from '../i18n/flags'

export default function LoginPage() {
  const navigate = useNavigate()
  const { t } = useT()
  const { lang, toggleLang } = useLanguage()
  const { login, isAuthenticated, isLoading, error, clearError } = useAuth()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Redirect if already authenticated
  if (isAuthenticated) {
    return <Navigate to="/" replace />
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!username.trim() || !password.trim()) {
      return
    }

    setIsSubmitting(true)
    clearError()

    try {
      await login(username, password)
      navigate('/')
    } catch (err) {
      // Error is already set in auth context
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        bgcolor: 'var(--bg)',
        p: 2,
      }}
    >
      <Card
        sx={{
          maxWidth: 400,
          width: '100%',
          bgcolor: 'var(--paper)',
          border: '1px solid var(--line)',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {/* The header — and with it the language switch — only exists behind
              the login, so on a first visit this is the one place to change it.
              Right-aligned above the wordmark, out of the sign-in flow. */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <Button
              size="small"
              onClick={toggleLang}
              title={lang === 'en' ? 'Zu Deutsch wechseln' : 'Switch to English'}
              aria-label={
                lang === 'en' ? 'Sprache auf Deutsch umstellen' : 'Switch language to English'
              }
              sx={{ minWidth: 0, gap: 0.75, color: 'var(--muted)', fontSize: 12, fontWeight: 700 }}
            >
              {lang === 'de' ? <FlagDE size={18} /> : <FlagEN size={18} />}
              {lang === 'de' ? 'DE' : 'EN'}
            </Button>
          </Box>

          <Typography
            component="h1"
            align="center"
            sx={{ fontSize: 22, fontWeight: 700, letterSpacing: '1.6px', color: 'var(--text)' }}
          >
            {import.meta.env.VITE_APP_NAME || 'FLIXALIO'}
          </Typography>

          <Typography
            variant="body2"
            align="center"
            sx={{ mb: 3, mt: 0.5, color: 'var(--muted)' }}
          >
            {t('Sign in with your FLIXALIO account')}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              autoFocus
              fullWidth
              label={t('Username')}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={isSubmitting || isLoading}
              margin="normal"
              required
              autoComplete="username"
            />

            <TextField
              fullWidth
              label={t('Password')}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isSubmitting || isLoading}
              margin="normal"
              required
              autoComplete="current-password"
              sx={{ mb: 3 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isSubmitting || isLoading}
              sx={{ mb: 2 }}
            >
              {isSubmitting ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  {t('Signing in …')}
                </>
              ) : (
                t('Sign in')
              )}
            </Button>
          </Box>

          <Typography variant="caption" color="text.secondary" display="block" align="center">
            {t('Contact your administrator to get an account')}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  )
}
